import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Divider,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    getArchetypeGuideEditors,
    addArchetypeGuideEditor,
    removeArchetypeGuideEditor,
    getAllUsers,
    migrateGuideEditorsTable
} from '../../services/tracker-limited-data';

function GuideEditorManager({ open, onClose, archetype }) {
    const [editors, setEditors] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [migrating, setMigrating] = useState(false);
    const [tableReady, setTableReady] = useState(true);

    const loadEditors = useCallback(async () => {
        if (!archetype?.id) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getArchetypeGuideEditors(archetype.id);
            setEditors(data || []);
            setTableReady(true);
        } catch (err) {
            if (err?.message?.includes('not initialized')) {
                setTableReady(false);
            } else {
                setError('Nie udało się załadować listy edytorów.');
            }
        } finally {
            setLoading(false);
        }
    }, [archetype?.id]);

    const loadAllUsers = async () => {
        try {
            const data = await getAllUsers();
            setAllUsers(data || []);
        } catch {
            // non-critical
        }
    };

    useEffect(() => {
        if (!open || !archetype?.id) return;
        migrateGuideEditorsTable()
            .then(() => {
                setTableReady(true);
                loadEditors();
            })
            .catch(() => {
                setTableReady(false);
            });
        loadAllUsers();
    }, [open, archetype?.id, loadEditors]);

    const handleMigrate = async () => {
        setMigrating(true);
        setError(null);
        try {
            await migrateGuideEditorsTable();
            setTableReady(true);
            await loadEditors();
        } catch (err) {
            setError(`Błąd migracji: ${err?.message || 'Nieznany błąd'}`);
        } finally {
            setMigrating(false);
        }
    };

    const handleAdd = async (userUid) => {
        setAdding(true);
        setError(null);
        try {
            await addArchetypeGuideEditor(archetype.id, userUid);
            await loadEditors();
            setSearchTerm('');
        } catch (err) {
            if (err?.message?.includes('not initialized')) {
                setTableReady(false);
            } else {
                setError(`Błąd dodawania: ${err?.message || 'Nieznany błąd'}`);
            }
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (userUid) => {
        setError(null);
        try {
            await removeArchetypeGuideEditor(archetype.id, userUid);
            setEditors((prev) => prev.filter((e) => e.user_uid !== userUid));
        } catch (err) {
            setError(`Błąd usuwania: ${err?.message || 'Nieznany błąd'}`);
        }
    };

    const editorUids = new Set(editors.map((e) => e.user_uid));

    const filteredUsers = allUsers.filter((u) => {
        if (editorUids.has(u.uid)) return false;
        const term = searchTerm.toLowerCase();
        return (
            u.display_name?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term)
        );
    });

    const archetypeName = archetype?.display_name || archetype?.name || archetype?.id;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edytorzy poradnika: {archetypeName}
            </DialogTitle>
            <DialogContent>
                {!tableReady && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                            <Button
                                size="small"
                                onClick={handleMigrate}
                                disabled={migrating}
                            >
                                {migrating ? 'Inicjalizacja...' : 'Inicjalizuj'}
                            </Button>
                        }
                    >
                        Tabela uprawnień nie istnieje. Kliknij "Inicjalizuj" aby ją utworzyć.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Aktualni edytorzy ({editors.length})
                        </Typography>

                        {editors.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Brak przypisanych edytorów. Poradnik może edytować tylko admin.
                            </Typography>
                        ) : (
                            <List dense sx={{ mb: 2 }}>
                                {editors.map((editor) => (
                                    <ListItem key={editor.user_uid} disableGutters>
                                        <ListItemText
                                            primary={editor.display_name || editor.user_uid}
                                            secondary={editor.email}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Usuń uprawnienie">
                                                <IconButton
                                                    size="small"
                                                    edge="end"
                                                    onClick={() => handleRemove(editor.user_uid)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {tableReady && (
                            <>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Dodaj edytora
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Szukaj po nazwie lub emailu..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                {searchTerm.trim().length > 0 && (
                                    <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
                                        {filteredUsers.length === 0 ? (
                                            <ListItem>
                                                <ListItemText secondary="Brak wyników" />
                                            </ListItem>
                                        ) : (
                                            filteredUsers.slice(0, 20).map((user) => (
                                                <ListItem key={user.uid} disableGutters sx={{ px: 1 }}>
                                                    <ListItemText
                                                        primary={user.display_name || user.uid}
                                                        secondary={user.email}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title="Nadaj uprawnienie">
                                                            <IconButton
                                                                size="small"
                                                                edge="end"
                                                                onClick={() => handleAdd(user.uid)}
                                                                disabled={adding}
                                                            >
                                                                <PersonAddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zamknij</Button>
            </DialogActions>
        </Dialog>
    );
}

export default GuideEditorManager;
